'use client'

import { useState, useEffect } from 'react'
import { Title, Text, Stack, SimpleGrid, Button } from '@mantine/core'
import Section from '@/components/ui/Section'
import Link from 'next/link'
import { Avatar } from '@/components/Avatar'
import { IconExternalLink } from '@tabler/icons-react'
import { UserWithMentor } from '@/types/mentor'

export default function FeaturedMentorsSection() {
  const [topMentors, setTopMentors] = useState<UserWithMentor[]>([])
  
  useEffect(() => {
    fetch('/api/mentors?limit=4')
      .then(res => res.json())
      .then(data => setTopMentors(data))
      .catch(err => console.error('Failed to load mentors:', err))
  }, [])

  return (
    <Section bg="white" py="lg" contentWidth="lg">
      <Stack gap="md">
        <Title order={1} size="h1" className="gradient-emerald" ta="center">
          You&apos;ll be in great company.
        </Title>
        <Text size="sm" c="gray.7" ta="center" maw={600} mx="auto">
          See who&apos;s already making an impact on our platform:
        </Text>
        <div style={{ paddingTop: '0.5rem', maxWidth: '900px', margin: '0 auto', width: '100%' }}>
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
            {topMentors.map((user, index) => (
              <Link key={user.id || index} href={`/mentors/${user.mentor?.handle ?? ''}`} style={{ textDecoration: 'none' }}>
                <div
                  style={{ 
                    cursor: 'pointer', 
                    backgroundColor: 'white',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    height: '100%',
                    borderRadius: '8px',
                    padding: '1rem',
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Stack align="center" gap="xs" style={{ minWidth: 0, width: '100%' }}>
                    <Avatar
                      radius={100}
                      size={80}
                      userId={user.id}
                      avatarId={user.avatarId}
                      loading="lazy"
                      priority={false}
                    />
                    <Title order={4} size="h5" ta="center" c="navy.8" style={{ marginTop: '0.25rem', wordBreak: 'break-word', width: '100%' }}>
                      {user.name}
                    </Title>
                    {user.mentor?.bio && (
                      <Text
                        size="xs"
                        c="dimmed"
                        ta="center"
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.4,
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          hyphens: 'auto',
                          width: '100%',
                        }}
                      >
                        {user.mentor.bio}
                      </Text>
                    )}
                  </Stack>
                </div>
              </Link>
            ))}
          </SimpleGrid>
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <Link href="/mentors" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <Button size="sm" variant="subtle" color="gray" rightSection={<IconExternalLink size={14} />}>
                See more mentors
              </Button>
            </Link>
          </div>
        </div>
      </Stack>
    </Section>
  )
}
